import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ACTIVITY_ICON } from '../../lib/activityIcon';
import { activityActor, activityText } from '../../lib/activityText';
import { useMarkNotificationsSeen, useNotifications } from '../../queries';
import type { ActivityItem } from '../../types';
import { Icon } from '@/shared/ui/atoms/Icon';
import { formatRelativeTime } from '@/shared/utils/time';
import './NotificationsMenu.styl';

export function NotificationsMenu() {
  const { data } = useNotifications();
  const { t } = useTranslation(undefined, { keyPrefix: 'notifications' });
  const { t: tRoot } = useTranslation();
  const seen = useMarkNotificationsSeen();
  const [open, setOpen] = useState(false);
  // what was unread when the panel opened: it stays on screen while open; opening marks it read, so it is not listed
  // again (the server only returns unread notifications)
  const [shown, setShown] = useState<ActivityItem[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const unread = data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const items = data?.items ?? [];
      setShown(items);
      if (items.length > 0 || unread > 0) seen.mutate(items[0]?.occurredAt);
    }
  };

  return (
    <div className="f-notif" ref={ref}>
      <button type="button" className="f-notif__btn" aria-label={unread > 0 ? t('labelUnread', { count: unread }) : t('label')} aria-expanded={open} data-unread={unread} onClick={toggle}>
        <Icon name="bellRing" size={16} />
        {unread > 0 ? (
          <span className="f-notif__badge" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="f-notif__panel" role="region" aria-label={t('recent')}>
          <span className="f-notif__shape f-notif__shape--rings" aria-hidden="true" />
          <span className="f-notif__shape f-notif__shape--dots" aria-hidden="true" />
          <div className="f-notif__head">
            <div className="f-notif__title">{t('recent')}</div>
            {shown.length > 0 ? <span className="f-notif__count">{t('newCount', { count: unread > shown.length ? unread : shown.length })}</span> : null}
          </div>
          {shown.length > 0 ? (
            <ul className="f-notif__list">
              {shown.map((n, i) => {
                const meta = ACTIVITY_ICON[n.type] ?? ACTIVITY_ICON.COMMENT;
                return (
                  <li key={n.id} className="f-notif__item f-notif__item--new" style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}>
                    <span className={`f-notif__icon f-notif__icon--${meta.tone}`} aria-hidden="true">
                      <Icon name={meta.icon} size={16} />
                    </span>
                    <div className="f-notif__body">
                      <div className="f-notif__text">{activityText(tRoot, n)}</div>
                      <div className="f-notif__meta">{activityActor(tRoot, n)}</div>
                    </div>
                    <time className="f-notif__time" dateTime={n.occurredAt}>
                      {formatRelativeTime(n.occurredAt)}
                    </time>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="f-notif__empty">{t('empty')}</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
